import React, { useState, useRef, useEffect } from "react";
import styles from "./CustomInput.module.scss";
import classNames from "classnames/bind";
import { ArrowIcon, ArrowLeft2Icon, SearchIcon } from "~/assets/icons";

const cx = classNames.bind(styles);

interface CustomInputProps {
  type: string;
  value?: string;
  onChange?: (value: string) => void;
  selectOptions?: string[];
  selectedOption?: string;
  onSelectChange?: (value: string) => void;
  placeholder?: string;
  selectPlaceholder?: string;
  onBackClick?: () => void;
  disable?: boolean;
}

const CustomInput: React.FC<CustomInputProps> = ({
  type,
  value,
  onChange,
  selectOptions,
  selectedOption,
  onSelectChange,
  placeholder = "Enter text",
  selectPlaceholder = "Select",
  onBackClick,
  disable = false,
}) => {
  const [showList, setShowList] = useState(false);
  const [selectedValue, setSelectedValue] = useState(selectedOption || "");
  const selectRef = useRef<HTMLDivElement>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setShowList(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Update selected value when prop changes
  useEffect(() => {
    if (selectedOption !== undefined) {
      setSelectedValue(selectedOption);
    }
  }, [selectedOption]);

  const handleSelectBoxClick = () => {
    setShowList((prev) => !prev);
  };

  const handleOptionSelect = (option: string) => {
    setSelectedValue(option);
    setShowList(false);
    if (onSelectChange) {
      onSelectChange(option);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e.target.value);
  };

  return (
    <div className={cx("custom-input-container")}>
      {type === "select" && (
        <div className={cx("select-box")} ref={selectRef}>
          <div
            className={cx("select-trigger", { open: showList })}
            onClick={handleSelectBoxClick}
          >
            <span
              className={cx("select-value", { "has-value": !!selectedValue })}
            >
              {selectedValue || selectPlaceholder}
            </span>
            <ArrowIcon className={cx("arrow-icon", { rotated: showList })} />
          </div>

          {showList && (
            <div className={cx("dropdown-list")}>
              {selectOptions?.map((option, index) => (
                <div
                  key={index}
                  className={cx("dropdown-item", {
                    selected: option === selectedValue,
                  })}
                  onClick={() => handleOptionSelect(option)}
                >
                  {option}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {type === "input" && (
        <input
          type="text"
          className={cx("input")}
          value={value}
          onChange={handleInputChange}
          placeholder={placeholder}
          disabled={disable}
        />
      )}

      {type === "searchLocation" && (
        <div className={cx("search-location-container")}>
          <button className={cx("back-button")} onClick={onBackClick}>
            <ArrowLeft2Icon className={cx("arrow-left-icon")} />
          </button>
          <input
            type="text"
            className={cx("input")}
            value={value}
            onChange={handleInputChange}
            placeholder={placeholder}
          />
          <SearchIcon className={cx("search-icon")} />
        </div>
      )}
    </div>
  );
};

export default CustomInput;
